import { motion } from "framer-motion"
import {
  RiArrowLeftLine,
  RiArrowRightLine,
  RiCheckboxCircleFill,
} from "@remixicon/react"
import { Link, Navigate, useParams } from "react-router-dom"

import { ColorBand, ColorBandInner } from "@/components/landing/ColorBand"
import { fadeIn, fadeUp, inView } from "@/components/landing/marketing-motion"
import { Button } from "@/components/ui/button"
import { isSolutionSlug, solutions } from "@/content/solutions"
import { useStartCta } from "@/hooks/useAppSession"

export function SolutionDetailPage() {
  const start = useStartCta("Start for free")
  const { slug } = useParams<{ slug: string }>()
  if (!slug || !isSolutionSlug(slug)) {
    return <Navigate to="/solutions" replace />
  }
  const s = solutions[slug]

  return (
    <div>
      <ColorBand className="bg-[#be9afc] pt-28 text-primary sm:pt-32 lg:pt-36">
        <div
          className="pointer-events-none absolute -right-20 top-10 size-80 rounded-full bg-white/20 blur-3xl"
          aria-hidden
        />
        <ColorBandInner>
          <Link
            to="/solutions"
            className="text-major inline-flex items-center gap-1.5 text-sm font-semibold text-primary/80 hover:text-primary hover:underline"
          >
            <RiArrowLeftLine className="size-4" />
            All solutions
          </Link>
          <motion.p
            {...fadeUp}
            className="font-heading mt-8 text-sm font-semibold uppercase tracking-[0.18em] text-primary/70"
          >
            Solutions
          </motion.p>
          <motion.h1
            {...fadeUp}
            className="font-display mt-3 max-w-3xl text-4xl leading-[0.92] sm:text-5xl lg:text-[3.4rem]"
          >
            {s.title}
          </motion.h1>
          <motion.p
            {...fadeUp}
            className="mt-5 max-w-2xl text-base font-semibold leading-relaxed text-primary/75 xl:text-lg"
          >
            {s.tagline}
          </motion.p>
        </ColorBandInner>
      </ColorBand>

      <ColorBand className="bg-white text-primary">
        <ColorBandInner>
          <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div
              {...fadeIn}
              className="overflow-hidden rounded-[2.5rem]"
            >
              <img
                src={s.image}
                alt=""
                className="aspect-4/3 w-full object-cover lg:aspect-auto lg:min-h-[380px]"
              />
            </motion.div>
            <motion.div {...fadeUp} className="space-y-8">
              <div>
                <h2 className="font-heading text-xl text-primary">
                  Why this matters
                </h2>
                <p className="mt-3 text-base font-semibold leading-relaxed text-primary/70">
                  {s.intro}
                </p>
              </div>
              <div>
                <h2 className="font-heading text-xl text-primary">
                  Sound familiar?
                </h2>
                <ul className="mt-4 space-y-3">
                  {s.painPoints.map((line) => (
                    <li
                      key={line}
                      className="flex gap-3 text-sm font-semibold text-primary/70"
                    >
                      <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" />
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </ColorBandInner>
      </ColorBand>

      <ColorBand className="bg-[#f6f4ef] text-primary">
        <ColorBandInner>
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
            <motion.div
              {...fadeIn}
              className="rounded-[2.5rem] bg-[#d4c4fc] p-8 sm:p-10"
            >
              <h2 className="font-heading text-xl text-primary sm:text-2xl">
                How Sqaleshop helps
              </h2>
              <ul className="mt-6 space-y-4">
                {s.howSqaleshopHelps.map((line) => (
                  <li
                    key={line}
                    className="flex gap-3 text-sm font-semibold text-primary"
                  >
                    <RiCheckboxCircleFill className="mt-0.5 size-5 shrink-0 text-primary" />
                    {line}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.06 }}
              className="rounded-[2.5rem] bg-white p-8 sm:p-10"
            >
              <h2 className="font-heading text-xl text-primary sm:text-2xl">
                What you can expect
              </h2>
              <ul className="mt-6 space-y-3">
                {s.outcomes.map((line, i) => (
                  <li
                    key={line}
                    className="flex gap-3 text-sm font-semibold text-primary/70"
                  >
                    <span className="font-display text-2xl leading-none text-primary/25">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {line}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={inView}
            className="mt-12 flex flex-wrap gap-3"
          >
            <Button
              size="xl"
              className="rounded-full px-8 font-semibold shadow-none"
              asChild
            >
              <Link to={start.href} className="gap-2">
                {start.label}
                <RiArrowRightLine className="size-4" />
              </Link>
            </Button>
            <Button
              size="xl"
              variant="outline"
              className="rounded-full px-8 font-semibold"
              asChild
            >
              <Link to="/pricing">Compare plans</Link>
            </Button>
          </motion.div>
        </ColorBandInner>
      </ColorBand>
    </div>
  )
}
